/**
 * <kazee-carousel> — a lightweight scroll-snap carousel.
 * Wires prev/next arrows and dot navigation to a horizontally scrolling track,
 * and reflects the active card + scroll-end state back onto the controls.
 */
if (!customElements.get('kazee-carousel')) {
  customElements.define(
    'kazee-carousel',
    class KazeeCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-kz-track]');
        if (!this.track) return;

        this.prevBtn = this.querySelector('[data-kz-prev]');
        this.nextBtn = this.querySelector('[data-kz-next]');
        this.dots = Array.from(this.querySelectorAll('[data-kz-dot]'));
        this.cards = Array.from(this.track.children);

        this.onScroll = this.onScroll.bind(this);
        this.update = this.update.bind(this);

        this.prevBtn?.addEventListener('click', () => this.scrollByCards(-1));
        this.nextBtn?.addEventListener('click', () => this.scrollByCards(1));
        this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        this.wireSwatches();

        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.track);
        this.update();
      }

      /**
       * Card swatches re-point a card at one of its variants — image, price and
       * both links — without leaving the carousel.
       */
      wireSwatches() {
        this.querySelectorAll('[data-kz-card-swatch]').forEach((sw) => {
          sw.addEventListener('click', () => {
            const card = sw.closest('.kz-pcard');
            if (!card) return;

            card
              .querySelectorAll('[data-kz-card-swatch]')
              .forEach((x) => x.classList.toggle('is-active', x === sw));

            const img = card.querySelector('.kz-pcard__img');
            const src = sw.dataset.img;
            if (img && src) {
              img.removeAttribute('srcset');
              img.src = src;
            }

            const price = card.querySelector('[data-kz-card-price]');
            if (price && sw.dataset.price) price.textContent = sw.dataset.price;

            const tag = card.querySelector('[data-kz-card-tag]');
            if (tag && sw.dataset.name) tag.textContent = sw.dataset.name;

            if (sw.dataset.url) {
              card.querySelectorAll('[data-kz-card-link]').forEach((a) => {
                a.href = sw.dataset.url;
              });
            }
          });
        });
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.onScroll);
        this.resizeObserver?.disconnect();
      }

      step() {
        if (this.cards.length > 1) {
          return this.cards[1].offsetLeft - this.cards[0].offsetLeft;
        }
        return this.cards[0] ? this.cards[0].offsetWidth : this.track.clientWidth;
      }

      /**
       * Scroll-snap containers ignore `behavior: 'smooth'` in some engines, so
       * the scroll silently never happens. Aim at an explicit offset and, if
       * nothing has moved shortly after, jump there outright.
       */
      scrollTarget(left) {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const target = Math.max(0, Math.min(left, max));
        const start = this.track.scrollLeft;
        if (Math.abs(target - start) < 1) return;

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        try {
          this.track.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
        } catch (e) {
          this.track.scrollLeft = target;
        }

        clearTimeout(this.fallbackTimer);
        this.fallbackTimer = setTimeout(() => {
          if (Math.abs(this.track.scrollLeft - start) < 1) this.track.scrollLeft = target;
        }, 150);
      }

      scrollByCards(direction) {
        this.scrollTarget(this.track.scrollLeft + direction * this.step());
      }

      goTo(index) {
        const card = this.cards[index];
        if (card) this.scrollTarget(card.offsetLeft - this.track.offsetLeft);
      }

      activeIndex() {
        const current = this.track.scrollLeft;
        let best = 0;
        let bestDistance = Infinity;
        this.cards.forEach((card, i) => {
          const distance = Math.abs(card.offsetLeft - this.track.offsetLeft - current);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = i;
          }
        });
        return best;
      }

      onScroll() {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(this.update);
      }

      update() {
        const index = this.activeIndex();
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));

        const maxScroll = this.track.scrollWidth - this.track.clientWidth - 1;
        const atStart = this.track.scrollLeft <= 1;
        const atEnd = this.track.scrollLeft >= maxScroll;
        if (this.prevBtn) this.prevBtn.disabled = atStart;
        if (this.nextBtn) this.nextBtn.disabled = atEnd;
      }
    }
  );
}
